import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { createBanner } from "../../../Api/BannerDetails/Banner.Api";
import { useAuth } from "../../../Store/AuthContext";
import useLoadingState from "../../../Utils/Hooks/useLodingState";
import useOverlayState from "../../../Utils/Hooks/useOverlayState";
import { SideDrawer } from "../../atoms/Drawer/SideDrawer";
import Label from "../../atoms/Input/Label";
import { TextInput } from "../../atoms/Input/TextInput";
import { LoadingButtonType } from "../../atoms/LoadingButton/LoadingButton";
import { NoRecordsFound } from "../../atoms/NoRecordsFound/NoRecordsFound";
import { FormItemsWrap } from "../../molecules/Wrap/Wrap";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { BannerUpload } from "./BannerUpload";
import { schema } from "./shema";

export const AddBanner = ({ close, data, OpenDrawer, mode, refetch }) => {
  const { state } = useAuth();
  const { t } = useTranslation();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const { OverlayState, closeOverlay } = useOverlayState();
  const [isError, setIsError] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [isLimitError, setIsLimitError] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");

  const {
    control,
    setValue,
    watch,
    setError,
    reset,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting, isDirty }, 
  } = useForm({
    defaultValues: {
      bannerName: "",
      videoLink: "",
      bannerImage: null,
    },
    mode: "onChange",
    resolver: yupResolver(schema(t)),
  });

  useEffect(() => {
    if ((mode === "update" || mode === "view") && data) {
      reset({
        bannerName: data.bannerName || "",
        videoLink: data.videoUrl || "",
        bannerImage: data.imagePath || null,
      });
    }
  }, [data, mode, reset]);

  const validateFormData = (values) => {
    const errorsArr = [];
    if (!values.bannerName || values.bannerName.trim() === "") {
      setError("bannerName", {
        type: "manual",
        message: t("validation:required-field"),
      });
      errorsArr.push("bannerName");
    }
    if (!values.bannerImage && mode !== "view") {
      setError("bannerImage", {
        type: "manual",
        message: t("validation:required-field"),
      });
      errorsArr.push("bannerImage");
    }
    if (values.videoLink && values.videoLink.trim() !== "") {
      try {
        const url = new URL(values.videoLink);
        if (
          !(url.hostname === "www.youtube.com" || url.hostname === "youtu.be")
        ) {
          setError("videoLink", {
            type: "manual",
            message: "Invalid URL. Please enter a valid YouTube link.",
          });
          errorsArr.push("videoLink");
        }
      } catch {
        setError("videoLink", {
          type: "manual",
          message: "Invalid URL. Please enter a valid YouTube link.",
        });
        errorsArr.push("videoLink");
      }
    }
    return errorsArr.length === 0;
  };

  const createBannerHandler = async (values) => {
    setIsError(false);
    if (!validateFormData(values)) return;
    startLoading({ btnName: "create" });
    const formData = new FormData();
    formData.append("bannerName", values.bannerName.trim());
    if (values.videoLink) formData.append("videoUrl", values.videoLink.trim());
    if (values.bannerImage) formData.append("bannerImage", values.bannerImage);
    formData.append("createdBy", state.userId || "");

    try {
      const response = await createBanner(formData);
      if (response.code === 200 && response.data && response.data.type === 0) {
        close();
        reset();
        if (refetch) refetch();
      } else if (response.data?.type === 2){
        setIsLimitError(true);
        setLimitMessage(response.message);
      } else {
        setIsError(true);
      }
    } catch {
      setIsError(true);
    } finally {
      stopLoading();
    }
  };

  const onSubmit = async (values) => {
    if (mode === "update") {
    } else if (mode === "create") {
      await createBannerHandler(values);
    }
  };

  const onError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) element.focus();
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setCancelConfirmOpen(true);
    } else {
      close();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    close();
  };

  return (
    <>
      <SideDrawer
        isLoading={LoadingState.visible}
        headingText={
          mode === "update"
            ? "Update Banner"
            : mode === "view"
            ? "View Banner"
            : "New Banner"
        }
        width={"690px"}
        openDrawer={OpenDrawer}
        close={handleCancel}
        anchorDrawer={"right"}
      >
        {isError && (
          <NoRecordsFound
            isError={isError}
            textPosition={"start"}
            padding={"8px"}
            textColor={"black"}
            label={t("validation:server-error")}
          />
        )}
        {isLimitError && (
          <NoRecordsFound
            isError={true}
            textPosition={"start"}
            padding={"8px"}
            textColor={"red"}
            label={limitMessage}
          />
        )}
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Stack rowGap={"20px"} width={"640px"}>
            <TextInput
              required={true}
              name={"bannerName"}
              control={control}
              label={"Banner name"}
              placeholder={""}
              error={!!formErrors.bannerName}
              helperText={formErrors.bannerName?.message}
              disabled={mode === "view"}
            />
            <div>
              {mode === "view" && data?.imagePath ? (
                <>
                  <Label label="Banner image" required={true} marginRight="0px" />
                  <img
                    src={data.imagePath}
                    alt={data.bannerName}
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                    }}
                  />
                </>
              ) : (
                <BannerUpload
                  control={control}
                  setValue={setValue}
                  watch={watch}
                  setError={setError}
                  label={"Banner image"}
                  required={true}
                  error={!!formErrors.bannerImage}
                  helperText={formErrors.bannerImage?.message}
                />
              )}
            </div>
            <TextInput
              required={false}
              name={"videoLink"}
              control={control}
              label={"Video link"}
              placeholder={""}
              error={!!formErrors.videoLink}
              helperText={formErrors.videoLink?.message}
              disabled={mode === "view"}
            />
            <FormItemsWrap
              direction={"row"}
              justifyContent={"flex-end"}
              gap={"16px"}
            >
              {mode !== "view" && (
                <Button
                  variant="whiteSquare"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              )}
              {mode !== "view" && (
                <LoadingButtonType
                  variant="primaryBlue"
                  type="submit"
                  loading={LoadingState.visible || isSubmitting}
                  disabled={isSubmitting}
                >
                  {mode === "update" ? "Update Banner" : "Create Banner"}
                </LoadingButtonType>
              )}
            </FormItemsWrap>
          </Stack>
        </form>
      </SideDrawer>
      {/* Render the confirmation dialog */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        close={() => setCancelConfirmOpen(false)}
        title={"Cancel"}
        description={"Are you sure you want to proceed?"}
        onOkTitle={"Yes"}
        onNoTitle={"No"}
        onOkClick={handleConfirmCancel}
        onNoClick={() => setCancelConfirmOpen(false)}
      />
    </>
  );
};